import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from 'src/helpers';
import { generatePath } from 'react-router-dom';
import { Customer } from 'src/pages/Dashboard/CreateEditMachineGroupForm';
import { MachineGroup } from 'src/pages/Dashboard/MachineGroups';
import {
  Machine,
  SyncSummary,
} from 'src/pages/MachineGroupDetails/MachineGroupMachines';
import {
  Preset,
  PresetsSummary,
} from 'src/pages/MachineGroupDetails/MachineGroupPresets';
import { ValidationResult } from 'src/pages/MachineGroupDetails/UploadPresetsForm';
import {
  PresetFullModel,
  PresetMediaItem,
} from 'src/pages/MachineGroupDetails/UpsertPresetForm';
import { FolderItem } from 'src/pages/MediaManagement/FolderAccordion';
import { User } from 'src/pages/AdminDashboard/UsersPanel';
import { MachineAnalytics } from 'src/pages/MachineGroupDetails/MachineTelemetry';
import { RoleAssignment, SecurityEvent, UserProfile } from './types';

type DownloadedFile = {
  blob: Blob;
  fileName: string;
};

export type ExportPresetsValidationIssue = {
  presetPartNumber: string;
  presetName: string;
  stepOrder?: number | null;
  propertyName: string;
  value: string;
};

export type ExportPresetsValidationResult = {
  issues: ExportPresetsValidationIssue[];
  hasIssues: boolean;
};

const getFileNameFromContentDisposition = (
  contentDisposition?: string | null,
) => {
  if (!contentDisposition) {
    return undefined;
  }

  const encodedFileNameMatch = contentDisposition.match(
    /filename\*=UTF-8''([^;]+)/i,
  );
  if (encodedFileNameMatch?.[1]) {
    return decodeURIComponent(encodedFileNameMatch[1]);
  }

  const fileNameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  return fileNameMatch?.[1];
};

export const backendApi = createApi({
  reducerPath: 'backendApi',
  baseQuery: baseQuery(),
  endpoints: ({ query, mutation }) => ({
    getUserProfile: query({
      query: () => ({
        url: '/userProfile/me',
        method: 'GET',
      }),
      transformResponse: (res: { data: UserProfile }) => ({
        data: res.data,
      }),
    }),
    getSecurityLog: query({
      query: ({
        startDate,
        endDate,
        serials,
        groupNames,
        eventTypes,
        take,
        page,
      }) => ({
        url: '/device/securityLog',
        method: 'GET',
        queryParams: {
          startDate,
          endDate,
          serials,
          groupNames,
          eventTypes,
          take,
          page,
        },
      }),
      transformResponse: (res: { data: SecurityEvent[] }) => ({
        data: res.data,
      }),
    }),
    listAllUserRoles: query({
      query: () => ({
        url: '/user/allRoles',
        method: 'GET',
      }),
      transformResponse: (res: { data: RoleAssignment[] }) => ({
        data: res.data,
      }),
    }),
    listMachineGroups: query({
      query: ({ filter, page, take }) => ({
        url: '/machineGroup',
        method: 'GET',
        queryParams: {
          filter,
          page,
          take,
        },
      }),
      transformResponse: (res: { data: MachineGroup[] }) => ({
        data: res.data,
      }),
    }),
    getMachineGroup: query({
      query: (id) => ({
        url: generatePath('/machineGroup/:id', { id }),
        method: 'GET',
      }),
      transformResponse: (res: { data: MachineGroup }) => ({
        data: res.data,
      }),
    }),
    getPresetsSummary: query({
      query: (id) => ({
        url: generatePath('/machineGroup/:id/presetsSummary', { id }),
        method: 'GET',
      }),
      transformResponse: (res: { data: PresetsSummary }) => ({
        data: res.data,
      }),
    }),
    listMachines: query({
      query: ({ machineGroupId, filter, page, take }) => ({
        url: '/machine',
        method: 'GET',
        queryParams: {
          machineGroupId,
          filter,
          page,
          take,
        },
      }),
      transformResponse: (res: { data: Machine[] }) => ({
        data: res.data,
      }),
    }),
    listCustomers: query({
      query: (includeDeleted) => ({
        url: '/customer',
        method: 'GET',
        queryParams: {
          includeDeleted,
        },
      }),
      transformResponse: (res: { data: Customer[] }) => ({
        data: res.data,
      }),
    }),
    listPresets: query({
      query: ({ machineGroupId, filter, page, take, presetIds }) => ({
        url: generatePath('/machineGroup/:id/presets', { id: machineGroupId }),
        method: 'GET',
        queryParams: {
          filter,
          page,
          take,
          presetIds,
        },
      }),
      transformResponse: (res: { data: Preset[] }) => ({
        data: res.data,
      }),
    }),
    exportPresetsCsv: mutation<
      DownloadedFile,
      { id: string; presetIds?: string[]; skipOptionalProperties?: boolean }
    >({
      queryFn: async (
        { id, presetIds, skipOptionalProperties },
        _api,
        _extraOptions,
        baseQueryFn,
      ) => {
        const result = await baseQueryFn({
          url: generatePath('/machineGroup/:id/presets/export', { id }),
          method: 'POST',
          body: {
            presetIds,
            skipOptionalProperties,
          },
          responseHandler: async (response) => response.blob(),
        });

        if (result.error) {
          return { error: result.error };
        }

        const response = (result.meta as { response?: Response } | undefined)
          ?.response;
        const fileName =
          getFileNameFromContentDisposition(
            response?.headers.get('content-disposition'),
          ) ?? 'presets.csv';

        return {
          data: {
            blob: result.data as Blob,
            fileName,
          },
        };
      },
    }),
    validateExportPresets: mutation<
      { data: ExportPresetsValidationResult },
      { id: string; presetIds?: string[] }
    >({
      query: ({ id, presetIds }) => ({
        url: generatePath('/machineGroup/:id/presets/export/validate', { id }),
        method: 'POST',
        body: {
          presetIds,
        },
        contentType: 'application/json',
      }),
      transformResponse: (res: { data: ExportPresetsValidationResult }) => ({
        data: res.data,
      }),
    }),
    syncSummary: query({
      query: (groupId) => ({
        url: generatePath('/machineGroup/:groupId/presetsync/summary', {
          groupId,
        }),
        method: 'GET',
      }),
      transformResponse: (res: { data: SyncSummary }) => ({
        data: res.data,
      }),
    }),
    presetSyncStatus: query({
      query: (groupId) => ({
        url: generatePath('/machineGroup/:groupId/presets/syncstatus', {
          groupId,
        }),
        method: 'GET',
      }),
      transformResponse: (res: { data: { [id: string]: boolean } }) => ({
        data: res.data,
      }),
    }),
    singlePreset: query({
      query: ({ groupId, presetId }) => ({
        url: generatePath('/machineGroup/:id/presets/:presetId', {
          id: groupId,
          presetId,
        }),
        method: 'GET',
      }),
      transformResponse: (res: { data: PresetFullModel }) => ({
        data: res.data,
      }),
    }),
    listMedia: query({
      query: (specificCustomerId?: string) => ({
        url: '/media',
        method: 'GET',
        queryParams: specificCustomerId ? { specificCustomerId } : {},
      }),
      transformResponse: (res: { data: PresetMediaItem[] }) => ({
        data: res.data,
      }),
    }),
    listFolders: query({
      query: (specificCustomerId?: string) => ({
        url: '/folder',
        method: 'GET',
        queryParams: specificCustomerId ? { specificCustomerId } : {},
      }),
      transformResponse: (res: { data: FolderItem[] }) => ({
        data: res.data,
      }),
    }),
    listUsers: query({
      query: () => ({
        url: '/user',
        method: 'GET',
      }),
      transformResponse: (res: { data: User[] }) => ({
        data: res.data,
      }),
    }),
    upsertPreset: mutation({
      query: ({ groupId, upsertPresetRequest }) => ({
        url: generatePath('/machineGroup/:id/presets/upsert', {
          id: groupId,
        }),
        method: 'POST',
        body: upsertPresetRequest,
        contentType: 'application/json',
      }),
    }),
    updateUser: mutation({
      query: ({ userId, request }) => ({
        url: generatePath('/user/:userId', { userId }),
        method: 'PUT',
        body: request,
        contentType: 'application/json',
      }),
      transformResponse: (res: { data: RoleAssignment }) => res.data,
    }),
    updateUserDeletedStatus: mutation({
      query: ({ userId, isDeleted }) => ({
        url: generatePath('/user/:userId/deletedStatus', {
          userId,
        }),
        method: 'POST',
        queryParams: { isDeleted },
        contentType: 'application/json',
      }),
    }),
    createUser: mutation({
      query: (createUserRequest) => ({
        url: '/user',
        method: 'POST',
        body: createUserRequest,
        contentType: 'application/json',
      }),
    }),
    createMachineGroup: mutation({
      query: (createMachineGroupRequest) => ({
        url: '/machineGroup',
        method: 'POST',
        body: createMachineGroupRequest,
        contentType: 'application/json',
      }),
    }),
    updateMachineGroup: mutation({
      query: ({ id, updateMachineGroupRequest }) => ({
        url: generatePath('/machineGroup/:id', { id }),
        method: 'PUT',
        body: updateMachineGroupRequest,
        contentType: 'application/json',
      }),
    }),
    deleteMachineGroup: mutation({
      query: (id) => ({
        url: generatePath('/machineGroup/:id', { id }),
        method: 'DELETE',
      }),
    }),
    onboardMachine: mutation({
      query: ({ onboardMachineRequest }) => ({
        url: '/machine/onboard',
        method: 'POST',
        body: onboardMachineRequest,
      }),
    }),
    offboardMachines: mutation({
      query: (offboardMachinesRequest) => ({
        url: '/machine/offboard',
        body: offboardMachinesRequest,
        contentType: 'application/json',
        method: 'POST',
      }),
    }),
    getAnalytics: mutation({
      query: ({ id, analyticsDataRequest }) => ({
        url: generatePath('/machine/:id/analyticsData', { id }),
        method: 'POST',
        body: analyticsDataRequest,
        headers: {
          'content-type': 'application/json',
        },
      }),
      transformResponse: (res: { data: MachineAnalytics }) => res.data,
    }),
    validateFile: mutation({
      query: ({ id, formData }) => ({
        url: generatePath('/machineGroup/:id/presets/bulk-upload/validate', {
          id,
        }),
        method: 'POST',
        body: formData,
        contentType: 'application/json',
      }),
      transformResponse: (res: { data: ValidationResult }) => ({
        data: res.data,
      }),
    }),
    submitPresetsFile: mutation({
      query: ({ id, formData }) => ({
        url: generatePath('/machineGroup/:id/presets/bulk-upload', {
          id,
        }),
        method: 'POST',
        body: formData,
        contentType: 'application/json',
      }),
    }),
    syncPresets: mutation({
      query: ({ id, startPresetSyncRequest }) => ({
        url: generatePath('/machineGroup/:groupId/presetsync', {
          groupId: id,
        }),
        method: 'POST',
        body: startPresetSyncRequest,
        contentType: 'application/json',
      }),
    }),
    archivePreset: mutation({
      query: ({ groupId, presetIds }) => ({
        url: generatePath('/machineGroup/:groupId/presets/archive', {
          groupId,
        }),
        method: 'POST',
        body: { presetIds },
        contentType: 'application/json',
      }),
    }),
    unarchivePreset: mutation({
      query: ({ groupId, presetIds }) => ({
        url: generatePath('/machineGroup/:groupId/presets/unarchive', {
          groupId,
        }),
        method: 'POST',
        body: { presetIds },
        contentType: 'application/json',
      }),
    }),
    deletePreset: mutation({
      query: ({ groupId, presetId }) => ({
        url: generatePath('/machineGroup/:groupId/presets/:presetId/delete', {
          groupId,
          presetId,
        }),
        method: 'DELETE',
      }),
    }),
    cancelGroupSync: mutation({
      query: (groupId) => ({
        url: generatePath('/machineGroup/:groupId/presetsync', {
          groupId,
        }),
        method: 'DELETE',
      }),
    }),
    cancelMachineSync: mutation({
      query: ({ groupId, machineId }) => ({
        url: generatePath(
          '/machineGroup/:groupId/machine/:machineId/presetsync',
          {
            groupId,
            machineId,
          },
        ),
        method: 'DELETE',
      }),
    }),
    clearMachinePresetVersion: mutation({
      query: ({ groupId, clearMachinePresetVersionRequest }) => ({
        url: generatePath('/machineGroup/:groupId/clearMachinePresetVersion', {
          groupId,
        }),
        body: clearMachinePresetVersionRequest,
        contentType: 'application/json',
        method: 'POST',
      }),
    }),
    uploadMedia: mutation({
      query: (formData) => ({
        url: '/media',
        method: 'POST',
        body: formData,
        contentType: 'application/json',
      }),
      transformResponse: (res: { data: PresetMediaItem[] }) => ({
        data: res.data,
      }),
    }),
    deleteMedia: mutation({
      query: (mediaId) => ({
        url: generatePath('/media/:mediaId', {
          mediaId,
        }),
        method: 'DELETE',
      }),
    }),
    moveMediaToFolder: mutation({
      query: ({ mediaId, folderId }) => ({
        url: generatePath('/media/:mediaId', {
          mediaId,
        }),
        body: { folderId },
        method: 'POST',
        contentType: 'application/json',
      }),
    }),
    createFolder: mutation({
      query: (createFolderRequest) => ({
        url: '/folder',
        body: createFolderRequest,
        method: 'POST',
        contentType: 'application/json',
      }),
    }),
    deleteFolder: mutation({
      query: (folderId) => ({
        url: generatePath('/folder/:folderId', {
          folderId,
        }),
        method: 'DELETE',
        contentType: 'application/json',
      }),
    }),
    updateCustomer: mutation({
      query: ({ customerId, request }) => ({
        url: generatePath('/customer/:customerId', { customerId }),
        method: 'PUT',
        body: request,
        contentType: 'application/json',
      }),
      transformResponse: (res: { data: RoleAssignment }) => res.data,
    }),
    updateCustomerDeletedStatus: mutation({
      query: ({ customerId, request }) => ({
        url: generatePath('/customer/:customerId/deletedStatus', {
          customerId,
        }),
        method: 'POST',
        body: request,
        contentType: 'application/json',
      }),
    }),
    createCustomer: mutation({
      query: (createCustomerRequest) => ({
        url: '/customer',
        method: 'POST',
        body: createCustomerRequest,
        contentType: 'application/json',
      }),
    }),
  }),
});

export const {
  useLazyGetUserProfileQuery,
  useGetSecurityLogQuery,
  useListAllUserRolesQuery,
  useListMachineGroupsQuery,
  useGetMachineGroupQuery,
  useGetPresetsSummaryQuery,
  useListMachinesQuery,
  useListCustomersQuery,
  useListPresetsQuery,
  useExportPresetsCsvMutation,
  useValidateExportPresetsMutation,
  useSyncSummaryQuery,
  usePresetSyncStatusQuery,
  useLazySinglePresetQuery,
  useListMediaQuery,
  useListFoldersQuery,
  useListUsersQuery,
  useUpdateUserMutation,
  useUpdateUserDeletedStatusMutation,
  useCreateUserMutation,
  useCreateMachineGroupMutation,
  useUpdateMachineGroupMutation,
  useDeleteMachineGroupMutation,
  useOnboardMachineMutation,
  useOffboardMachinesMutation,
  useGetAnalyticsMutation,
  useValidateFileMutation,
  useSubmitPresetsFileMutation,
  useSyncPresetsMutation,
  useArchivePresetMutation,
  useUnarchivePresetMutation,
  useDeletePresetMutation,
  useCancelGroupSyncMutation,
  useCancelMachineSyncMutation,
  useClearMachinePresetVersionMutation,
  useUpsertPresetMutation,
  useUploadMediaMutation,
  useDeleteMediaMutation,
  useMoveMediaToFolderMutation,
  useCreateFolderMutation,
  useDeleteFolderMutation,
  useUpdateCustomerMutation,
  useUpdateCustomerDeletedStatusMutation,
  useCreateCustomerMutation,
} = backendApi;
